/**
 * MODULE: _shared/frontend-shell/src/design-system/CommandPalette/providers/contentProvider.tsx
 * PURPOSE: Sales Hub catalogue search via templates API (titles and prompts indexed server-side)
 * DEPENDENCIES: react, react-router-dom, @heroicons/react, @domain/templates/frontend, @domain/templates/contracts
 * PUBLIC_API: createContentPaletteProvider, contentTypeIcon
 * AGENT_SAFETY: SAFE
 */

import type { ReactNode } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import {
  BookOpenIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  PresentationChartBarIcon,
} from '@heroicons/react/24/outline';
import { templatesApi } from '@domain/templates/frontend';
import type { TemplateType } from '@domain/templates/contracts/templates.types';
import type { PaletteItem, PaletteProvider, PaletteSearchContext } from '../types';

export function contentTypeIcon(type: string): ReactNode {
  switch (type as TemplateType) {
    case 'presentation':
      return <PresentationChartBarIcon className="h-4 w-4 text-violet-500" aria-hidden />;
    case 'email':
      return <EnvelopeIcon className="h-4 w-4 text-sky-500" aria-hidden />;
    case 'event':
      return <CalendarDaysIcon className="h-4 w-4 text-amber-500" aria-hidden />;
    case 'sales_guide':
      return <BookOpenIcon className="h-4 w-4 text-emerald-500" aria-hidden />;
    default:
      return <DocumentTextIcon className="h-4 w-4 text-gray-400" aria-hidden />;
  }
}

const DEFAULT_MIN = 2;

export function createContentPaletteProvider(navigate: NavigateFunction): PaletteProvider {
  return {
    id: 'content',
    heading: 'Content',
    minQueryLength: DEFAULT_MIN,
    async search(query: string, { signal }: PaletteSearchContext): Promise<PaletteItem[]> {
      const q = query.trim();
      if (q.length < DEFAULT_MIN) return [];

      const rows = await templatesApi.search({ q, limit: 8, signal });
      return rows.map((r) => ({
        id: `content:${r.id}`,
        label: r.title,
        sublabel: r.type,
        icon: contentTypeIcon(r.type),
        onSelect: () => {
          navigate(`/sales-hub/content/${encodeURIComponent(r.id)}`);
        },
      }));
    },
  };
}
